import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card } from '../components/ui/card';
import { toast } from 'sonner';
import axios from 'axios';
import Header from '../components/Header';
import Footer from '../components/Footer';
import InsuranceUpload from '../components/InsuranceUpload';
import EquipmentUpload from '../components/EquipmentUpload';
import { Check, Plus, Trash2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CraneEquipmentSignup = ({ email, onComplete }) => {
  const [items, setItems] = useState([{ description: '', safe_lift_capacity: '' }]);
  const [saving, setSaving] = useState(false);

  const addItem = () => {
    if (items.length >= 100) { toast.error('Maximum 100 equipment items'); return; }
    setItems(prev => [...prev, { description: '', safe_lift_capacity: '' }]);
  };

  const removeItem = (index) => {
    if (items.length <= 1) return;
    setItems(prev => prev.filter((_, i) => i !== index));
  };

  const updateItem = (index, field, value) => {
    setItems(prev => prev.map((item, i) => i === index ? { ...item, [field]: value } : item));
  };

  const handleSave = async () => {
    const valid = items.filter(i => i.description.trim());
    if (!valid.length) { toast.error('Please add at least one equipment item'); return; }
    setSaving(true);
    try {
      for (const item of valid) {
        await axios.post(`${API}/crane-equipment`, { email, description: item.description, safe_lift_capacity: item.safe_lift_capacity });
      }
      toast.success(`${valid.length} equipment item(s) saved`);
      onComplete();
    } catch (err) {
      toast.error('Failed to save equipment');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="p-6">
      <h3 className="text-lg font-semibold text-[#1e3a5f] mb-4">Crane Equipment</h3>
      <div className="space-y-3">
        {items.map((item, i) => (
          <div key={i} className="flex gap-2 items-start">
            <div className="flex-1">
              <Label className="text-xs">Equipment Description</Label>
              <Input value={item.description} onChange={(e) => updateItem(i, 'description', e.target.value)} placeholder="e.g. Liebherr LTM 1300-6.2 Mobile Crane" />
            </div>
            <div className="w-40">
              <Label className="text-xs">Safe Lift Capacity</Label>
              <Input value={item.safe_lift_capacity} onChange={(e) => updateItem(i, 'safe_lift_capacity', e.target.value)} placeholder="e.g. 300t" />
            </div>
            <Button type="button" variant="outline" size="sm" onClick={() => removeItem(i)} className="mt-5 text-red-500" disabled={items.length <= 1}>
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        ))}
      </div>
      <div className="flex gap-3 mt-4">
        <Button type="button" variant="outline" onClick={addItem} className="text-sm">
          <Plus className="w-4 h-4 mr-1" /> Add Equipment
        </Button>
        <Button type="button" onClick={handleSave} disabled={saving} className="bg-green-600 hover:bg-green-700 text-white text-sm">
          {saving ? 'Saving...' : 'Save Equipment & Complete Registration'}
        </Button>
      </div>
      <p className="text-xs text-gray-400 mt-2">You can add up to 100 items. More can be added later from your dashboard.</p>
    </Card>
  );
};

const SignUp = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const role = searchParams.get('role') || 'shipper';
  
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1); // 1: Account, 2: Insurance, 3: Equipment, 4: Plan Selection
  const [formData, setFormData] = useState({
    email: '',
    name: '',
    phone: '',
    company_name: '',
    abn: '',
    password: '',
    confirm_password: '',
    role: role
  });
  const [baseLocations, setBaseLocations] = useState([{ suburb: '', state: 'NSW' }]);
  const [selectedPlan, setSelectedPlan] = useState('monthly');
  const [promoCode, setPromoCode] = useState('');
  const [promoValidated, setPromoValidated] = useState(false);
  const [promoMessage, setPromoMessage] = useState('');
  const [validatingPromo, setValidatingPromo] = useState(false);

  const subscriptionPlans = [
    {
      id: 'monthly',
      name: 'Monthly',
      price: 49,
      interval: 'per month',
      features: [
        'Unlimited load access',
        'Direct shipper contact',
        'Secure payments',
        'Email & phone support'
      ]
    },
    {
      id: 'quarterly',
      name: 'Quarterly',
      price: 129,
      interval: 'per quarter',
      savings: 'Save $18',
      features: [
        'Everything in Monthly',
        'Priority listing',
        '12% discount',
        'Extended support'
      ],
      popular: true
    },
    {
      id: 'yearly',
      name: 'Yearly',
      price: 499,
      interval: 'per year',
      savings: 'Save $89',
      features: [
        'Everything in Quarterly',
        'Premium badge',
        '15% discount',
        'Dedicated account manager'
      ]
    },
    {
      id: 'adhoc',
      name: 'Adhoc Member',
      price: 0,
      interval: 'No membership fee',
      savings: 'Pay per job only',
      features: [
        'No monthly or upfront fees',
        'Quote on any load',
        'Pay $49 only when your quote is accepted',
        'Access shipper details after payment'
      ],
      badge: 'Flexible'
    }
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const addBaseLocation = () => {
    if (baseLocations.length >= 12) {
      toast.error('Maximum 12 base locations allowed');
      return;
    }
    setBaseLocations(prev => [...prev, { suburb: '', state: 'NSW' }]);
  };

  const removeBaseLocation = (index) => {
    if (baseLocations.length <= 1) return;
    setBaseLocations(prev => prev.filter((_, i) => i !== index));
  };

  const updateBaseLocation = (index, field, value) => {
    setBaseLocations(prev => prev.map((loc, i) => i === index ? { ...loc, [field]: value } : loc));
  };

  const australianStates = ['NSW', 'VIC', 'QLD', 'WA', 'SA', 'TAS', 'NT', 'ACT'];

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.email || !formData.name || !formData.phone || !formData.password) {
      toast.error('Missing Information', {
        description: 'Please fill in all required fields'
      });
      return;
    }

    if (formData.password !== formData.confirm_password) {
      toast.error('Password Mismatch', {
        description: 'Passwords do not match'
      });
      return;
    }

    setLoading(true);
    try {
      // Create user account or add role to existing account
      const signupData = {
        email: formData.email,
        name: formData.name,
        phone: formData.phone,
        company_name: formData.company_name,
        password: formData.password,
        role: formData.role,
        subscription_plan: null  // Plan selected later in Step 4
      };
      
      const userResponse = await axios.post(`${API}/users/signup`, signupData);
      
      // Check if this was adding a new role to existing account
      const isRoleAddition = userResponse.data.role_added;
      
      // If carrier or crane operator, save email to localStorage for later steps and move to payment
      if (formData.role === 'carrier' || formData.role === 'crane_operator') {
        localStorage.setItem('signup_email', formData.email);
        
        // Save ABN and base locations
        const validLocations = baseLocations.filter(loc => loc.suburb.trim());
        if (formData.abn || validLocations.length > 0) {
          try {
            await axios.post(`${API}/users/${formData.email}/carrier-details`, {
              abn: formData.abn,
              base_locations: validLocations.map((loc, i) => ({
                suburb: loc.suburb,
                state: loc.state,
                is_primary: i === 0
              }))
            });
          } catch (err) {
            console.error('Failed to save carrier details:', err);
          }
        }
        
        if (isRoleAddition) {
          toast.success('Carrier role added! Please upload your insurance documents.');
        } else {
          toast.success('Account created! Please upload your insurance documents.');
        }
        setStep(2); // Move to insurance upload
      } else {
        // Shipper role
        if (isRoleAddition) {
          toast.success('Shipper role added! You can now post loads and manage quotes.');
          // Store roles in localStorage for role switcher
          localStorage.setItem('user_roles', JSON.stringify(userResponse.data.roles));
        } else {
          toast.success('Account created! You can now start posting loads.');
        }
        navigate('/');
      }
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to create account. Please try again.';
      toast.error('Signup Failed', {
        description: errorMsg
      });
      console.error('Signup error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePaymentComplete = async () => {
    // Final step: save plan choice and complete registration
    if (selectedPlan === 'adhoc') {
      setLoading(true);
      try {
        await axios.post(`${API}/users/${formData.email}/set-adhoc-status`);
        toast.success('Registration Complete!', {
          description: 'Your ad-hoc account is pending admin approval. You will be notified once approved. No membership fee required — you only pay $49 per accepted job.'
        });
        navigate('/');
      } catch (error) {
        toast.error('Error', {
          description: 'Failed to set adhoc status'
        });
      } finally {
        setLoading(false);
      }
      return;
    }
    
    // For subscription plans, save the choice — payment happens after admin approval
    setLoading(true);
    try {
      const params = new URLSearchParams({
        plan: selectedPlan
      });
      if (promoValidated && promoCode) {
        params.append('promo_code', promoCode);
      }
      await axios.post(`${API}/users/${formData.email}/save-subscription-choice?${params.toString()}`);
      toast.success('Registration Complete!', {
        description: promoValidated 
          ? `Your promo code has been applied. Your account is pending admin approval.`
          : 'Your account is pending admin approval. Once approved, you will be prompted to complete payment.'
      });
      navigate('/');
    } catch (error) {
      toast.error('Error', {
        description: 'Failed to save plan selection. Please try again.'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleInsuranceComplete = async () => {
    // After insurance, go to equipment step
    setStep(3);
  };

  const handleEquipmentComplete = async () => {
    // After equipment, go to plan selection step
    setStep(4);
  };

  const validatePromoCode = async () => {
    if (!promoCode.trim()) {
      toast.error('Please enter a promo code');
      return;
    }

    setValidatingPromo(true);
    try {
      const response = await axios.post(`${API}/promo-codes/validate`, {
        code: promoCode,
        user_email: formData.email
      });
      
      if (response.data.valid) {
        setPromoValidated(true);
        const trialMonths = response.data.promo_code.discount_value;
        setPromoMessage(`✓ Valid! You'll get ${trialMonths} month${trialMonths > 1 ? 's' : ''} free trial`);
        toast.success(`Promo code applied! ${trialMonths} month${trialMonths > 1 ? 's' : ''} free trial`);
      }
    } catch (error) {
      setPromoValidated(false);
      setPromoMessage('');
      const errorMsg = error.response?.data?.detail || 'Invalid promo code';
      toast.error(errorMsg);
    } finally {
      setValidatingPromo(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      <div className="container mx-auto px-4 sm:px-6 py-12">
        <div className="max-w-5xl mx-auto">
          {/* Progress Steps for Carriers */}
          {(role === 'carrier' || role === 'crane_operator') && (
            <div className="flex justify-center mb-8">
              <div className="flex items-center space-x-4">
                <div className={`flex items-center ${step >= 1 ? 'text-blue-600' : 'text-gray-400'}`}>
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center ${step >= 1 ? 'bg-blue-600 text-white' : 'bg-gray-300'}`}>
                    1
                  </div>
                  <span className="ml-2 font-medium">Account</span>
                </div>
                <div className="w-12 h-1 bg-gray-300"></div>
                <div className={`flex items-center ${step >= 2 ? 'text-blue-600' : 'text-gray-400'}`}>
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center ${step >= 2 ? 'bg-blue-600 text-white' : 'bg-gray-300'}`}>
                    2
                  </div>
                  <span className="ml-2 font-medium">Insurance</span>
                </div>
                <div className="w-12 h-1 bg-gray-300"></div>
                <div className={`flex items-center ${step >= 3 ? 'text-blue-600' : 'text-gray-400'}`}>
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center ${step >= 3 ? 'bg-blue-600 text-white' : 'bg-gray-300'}`}>
                    3
                  </div>
                  <span className="ml-2 font-medium">Equipment</span>
                </div>
                <div className="w-12 h-1 bg-gray-300"></div>
                <div className={`flex items-center ${step >= 4 ? 'text-blue-600' : 'text-gray-400'}`}>
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center ${step >= 4 ? 'bg-blue-600 text-white' : 'bg-gray-300'}`}>
                    4
                  </div>
                  <span className="ml-2 font-medium">Plan</span>
                </div>
              </div>
            </div>
          )}
          
          <h1 className="text-3xl font-bold text-[#1e3a5f] text-center mb-2">
            {role === 'carrier' ? 'Join as a Carrier' : role === 'crane_operator' ? 'Join as a Crane Operator' : 'Sign Up as a Shipper'}
          </h1>
          <p className="text-gray-600 text-center mb-8">
            {step === 1 && 'Create your account to get started'}
            {step === 2 && 'Upload your insurance documents to verify your credentials'}
            {step === 3 && (role === 'crane_operator' ? 'Register your crane equipment' : 'Register your vehicles and trailers')}
            {step === 4 && 'Choose your subscription plan'}
          </p>

          {/* Important Notice for Carriers / Crane Operators */}
          {(role === 'carrier' || role === 'crane_operator') && step === 1 && (
            <div className="mb-6 bg-blue-50 border-l-4 border-blue-500 p-4 rounded-r-lg">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-blue-500" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm font-semibold text-blue-800">
                    ⚠️ Important: Required to Quote on Loads
                  </p>
                  <p className="text-sm text-blue-700 mt-1">
                    To be eligible to quote on loads, you must complete ALL steps including:
                  </p>
                  <ul className="text-sm text-blue-700 mt-2 ml-4 list-disc space-y-1">
                    <li>Upload all required insurance documents and certificates</li>
                    <li>Register at least one vehicle/trailer with photos</li>
                    <li>Await admin approval before payment is required</li>
                  </ul>
                  <p className="text-sm text-blue-700 mt-2 font-medium">
                    No payment is taken until your account has been approved.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Step 1: Account Details */}
          {step === 1 && (
            <div className="grid lg:grid-cols-2 gap-8">
            {/* Sign Up Form */}
            <div className="bg-white rounded-lg shadow-md p-6 sm:p-8">
              <h2 className="text-xl font-semibold text-[#1e3a5f] mb-6">Account Details</h2>
              
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="name">Full Name *</Label>
                  <Input
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    placeholder="John Smith"
                  />
                </div>

                <div>
                  <Label htmlFor="email">Email *</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    placeholder="john@example.com"
                  />
                </div>

                <div>
                  <Label htmlFor="phone">Phone *</Label>
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    placeholder="04XX XXX XXX"
                  />
                </div>

                <div>
                  <Label htmlFor="company_name">Company Name</Label>
                  <Input
                    id="company_name"
                    name="company_name"
                    value={formData.company_name}
                    onChange={handleChange}
                    placeholder="Your Company Pty Ltd"
                  />
                </div>

                {/* ABN - Carrier only */}
                {(role === 'carrier' || role === 'crane_operator') && (
                  <div>
                    <Label htmlFor="abn">ABN (Australian Business Number)</Label>
                    <Input
                      id="abn"
                      name="abn"
                      value={formData.abn}
                      onChange={handleChange}
                      placeholder="XX XXX XXX XXX"
                      maxLength={14}
                      data-testid="carrier-abn-input"
                    />
                  </div>
                )}

                {/* Base Locations - Carrier only */}
                {(role === 'carrier' || role === 'crane_operator') && (
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <Label>Base Location(s)</Label>
                      <span className="text-xs text-gray-500">{baseLocations.length}/12</span>
                    </div>
                    {baseLocations.map((loc, index) => (
                      <div key={index} className="flex gap-2 items-end" data-testid={`base-location-${index}`}>
                        <div className="flex-1">
                          {index === 0 && <Label className="text-xs text-gray-500">Suburb/Town</Label>}
                          <Input
                            value={loc.suburb}
                            onChange={(e) => updateBaseLocation(index, 'suburb', e.target.value)}
                            placeholder="e.g. Parramatta"
                          />
                        </div>
                        <div className="w-24">
                          {index === 0 && <Label className="text-xs text-gray-500">State</Label>}
                          <select
                            value={loc.state}
                            onChange={(e) => updateBaseLocation(index, 'state', e.target.value)}
                            className="w-full h-10 border border-gray-300 rounded-md px-2 text-sm"
                          >
                            {australianStates.map(s => (
                              <option key={s} value={s}>{s}</option>
                            ))}
                          </select>
                        </div>
                        {baseLocations.length > 1 && (
                          <button
                            type="button"
                            onClick={() => removeBaseLocation(index)}
                            className="h-10 px-2 text-red-500 hover:text-red-700 text-lg"
                          >
                            &times;
                          </button>
                        )}
                      </div>
                    ))}
                    {baseLocations.length < 12 && (
                      <button
                        type="button"
                        onClick={addBaseLocation}
                        className="text-sm text-blue-600 hover:text-blue-800 font-medium"
                        data-testid="add-location-btn"
                      >
                        + Add another location
                      </button>
                    )}
                  </div>
                )}

                <div>
                  <Label htmlFor="password">Password *</Label>
                  <Input
                    id="password"
                    name="password"
                    type="password"
                    value={formData.password}
                    onChange={handleChange}
                    required
                    placeholder="••••••••"
                  />
                </div>

                <div>
                  <Label htmlFor="confirm_password">Confirm Password *</Label>
                  <Input
                    id="confirm_password"
                    name="confirm_password"
                    type="password"
                    value={formData.confirm_password}
                    onChange={handleChange}
                    required
                    placeholder="••••••••"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-red-600 hover:bg-red-700 text-white"
                >
                  {loading ? 'Creating Account...' : (role === 'carrier' || role === 'crane_operator') ? 'Continue to Insurance Upload' : 'Create Account'}
                </Button>

                <p className="text-sm text-gray-600 text-center">
                  Already have an account?{' '}
                  <button
                    type="button"
                    onClick={() => navigate('/login')}
                    className="text-red-600 hover:text-red-700 font-medium"
                  >
                    Log In
                  </button>
                </p>
              </form>
            </div>

            {/* Subscription Plans are in Step 4 */}
          </div>
          )}

          {/* Step 4: Plan Selection (Carriers and Crane Operators) */}
          {step === 4 && (role === 'carrier' || role === 'crane_operator') && (
            <div className="max-w-4xl mx-auto">
              <div className="bg-white rounded-lg shadow-md p-6 sm:p-8">
                <h2 className="text-xl font-semibold text-[#1e3a5f] mb-4">Choose Your Plan</h2>
                
                <div className="mb-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <p className="text-sm text-yellow-800">
                    <strong>Note:</strong> No payment is taken during signup. Your subscription will begin after your account is approved by our admin team. If you have a promo code, enter it below and it will be applied automatically.
                  </p>
                </div>

                <div className="space-y-4">
                  {subscriptionPlans.map((plan) => (
                    <Card
                      key={plan.id}
                      className={`p-6 cursor-pointer transition-all ${
                        selectedPlan === plan.id
                          ? 'border-2 border-red-600 shadow-lg'
                          : 'border border-gray-200 hover:border-gray-300'
                      }`}
                      onClick={() => {
                        setSelectedPlan(plan.id);
                        localStorage.setItem('selected_plan', plan.id);
                      }}
                    >
                      <div className="flex justify-between items-start mb-4">
                        <div>
                          <div className="flex items-center gap-2">
                            <h3 className="text-lg font-bold text-[#1e3a5f]">{plan.name}</h3>
                            {plan.popular && (
                              <span className="px-2 py-1 bg-red-100 text-red-600 text-xs font-semibold rounded">
                                Popular
                              </span>
                            )}
                          </div>
                          {plan.savings && (
                            <p className="text-sm text-green-600 font-medium">{plan.savings}</p>
                          )}
                        </div>
                        <div className="text-right">
                          <p className="text-2xl font-bold text-[#1e3a5f]">${plan.price}</p>
                          <p className="text-sm text-gray-600">{plan.interval}</p>
                        </div>
                      </div>
                      
                      <ul className="space-y-2">
                        {plan.features.map((feature, index) => (
                          <li key={index} className="flex items-start gap-2 text-sm text-gray-600">
                            <Check className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                            <span>{feature}</span>
                          </li>
                        ))}
                      </ul>
                    </Card>
                  ))}
                </div>

                <div className="mt-6 p-4 bg-blue-50 rounded-lg">
                  <p className="text-sm text-gray-700">
                    {selectedPlan === 'adhoc' ? (
                      <><strong>Adhoc Members:</strong> No membership fee is charged. You only pay $49 per job when your quote is accepted by a shipper.</>
                    ) : (
                      <><strong>Note:</strong> No payment is taken at signup. Once your account is approved by our admin team, you will be prompted to complete payment.</>
                    )}
                  </p>
                </div>

                {/* Promo Code Section */}
                <div className="mt-6">
                  <h3 className="text-lg font-semibold text-[#1e3a5f] mb-3">Have a Promo Code?</h3>
                  <div className="flex gap-3">
                    <div className="flex-1">
                      <Input
                        placeholder="Enter promo code"
                        value={promoCode}
                        onChange={(e) => {
                          setPromoCode(e.target.value.toUpperCase());
                          setPromoValidated(false);
                          setPromoMessage('');
                        }}
                        disabled={promoValidated}
                        className="uppercase"
                      />
                      {promoMessage && (
                        <p className={`text-sm mt-1 ${promoValidated ? 'text-green-600' : 'text-red-600'}`}>
                          {promoMessage}
                        </p>
                      )}
                    </div>
                    <Button
                      type="button"
                      onClick={validatePromoCode}
                      disabled={validatingPromo || promoValidated || !promoCode}
                      className="bg-blue-600 hover:bg-blue-700"
                    >
                      {validatingPromo ? 'Validating...' : promoValidated ? 'Applied ✓' : 'Apply'}
                    </Button>
                  </div>
                </div>

                {/* Complete Registration Button */}
                <div className="mt-8">
                  <Button
                    onClick={handlePaymentComplete}
                    disabled={loading}
                    className="w-full bg-red-600 hover:bg-red-700 text-white"
                    data-testid="complete-registration-btn"
                  >
                    {loading ? 'Processing...' : selectedPlan === 'adhoc' ? 'Complete Registration (No Payment Required)' : 'Complete Registration'}
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* Step 2: Insurance Upload (Carriers and Crane Operators) */}
          {step === 2 && (role === 'carrier' || role === 'crane_operator') && (
            <div className="max-w-4xl mx-auto">
              {role === 'crane_operator' ? (
                <Card className="p-6 mb-4 bg-blue-50 border border-blue-200">
                  <h3 className="font-semibold text-blue-900 mb-2">Required Insurance Documents</h3>
                  <p className="text-sm text-blue-800 mb-2">Please upload the following:</p>
                  <ul className="text-sm text-blue-800 list-disc ml-4 space-y-1">
                    <li>Public Liability Insurance</li>
                    <li>High Risk Licences</li>
                    <li>Workers Compensation Certificate</li>
                  </ul>
                </Card>
              ) : null}
              <InsuranceUpload 
                carrierEmail={formData.email} 
                onComplete={handleInsuranceComplete}
                isCraneOperator={role === 'crane_operator'}
              />
            </div>
          )}

          {/* Step 3: Equipment Upload (Carriers only - Optional) */}
          {step === 3 && role === 'carrier' && (
            <div className="max-w-4xl mx-auto">
              <Card className="p-6 mb-4 bg-blue-50 border border-blue-200">
                <h3 className="font-semibold text-blue-900 mb-2">Equipment Registration (Optional)</h3>
                <p className="text-sm text-blue-800 mb-2">
                  You can register your vehicles and trailers now or skip this step and add them later from your dashboard.
                </p>
                <Button
                  onClick={handleEquipmentComplete}
                  variant="outline"
                  className="mt-2"
                  disabled={loading}
                >
                  {loading ? 'Processing...' : 'Skip & Choose Plan →'}
                </Button>
              </Card>
              <EquipmentUpload 
                carrierEmail={formData.email} 
                onComplete={handleEquipmentComplete}
              />
            </div>
          )}

          {/* Step 3: Crane Equipment (Crane Operators) */}
          {step === 3 && role === 'crane_operator' && (
            <div className="max-w-4xl mx-auto">
              <Card className="p-6 mb-4 bg-blue-50 border border-blue-200">
                <h3 className="font-semibold text-blue-900 mb-2">Crane Equipment Registration</h3>
                <p className="text-sm text-blue-800 mb-2">
                  List your crane equipment below with the safe lift capacity for each asset. You can add up to 100 items.
                </p>
                <Button
                  onClick={handleEquipmentComplete}
                  variant="outline"
                  className="mt-2"
                  disabled={loading}
                >
                  {loading ? 'Processing...' : 'Skip & Choose Plan →'}
                </Button>
              </Card>
              <CraneEquipmentSignup email={formData.email} onComplete={handleEquipmentComplete} />
            </div>
          )}
        </div>
      </div>
      
      <Footer />
    </div>
  );
};

export default SignUp;
